import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Globe, KeyRound, Download, Send, Link2 } from "lucide-react";

export default function Sourcing() {
  const [connectors, setConnectors] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [keyFor, setKeyFor] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [importFor, setImportFor] = useState({ connector: "", requisition_id: "" });
  const [webUrl, setWebUrl] = useState("");
  const [webReq, setWebReq] = useState("");
  const [busy, setBusy] = useState("");
  const [postReq, setPostReq] = useState("");
  const [postBoards, setPostBoards] = useState(["naukri", "linkedin"]);

  const load = () => {
    api.get("/sourcing/connectors").then((r) => setConnectors(r.data));
    api.get("/sourcing/analytics").then((r) => setAnalytics(r.data));
    api.get("/requisitions?status=open").then((r) => {
      setReqs(r.data);
      if (r.data[0]) { setWebReq(r.data[0].id); setPostReq(r.data[0].id); setImportFor((f) => ({ ...f, requisition_id: r.data[0].id })); }
    });
  };
  useEffect(() => { load(); }, []);

  const configure = async (name) => {
    if (!apiKey) return toast.error("Enter the API key");
    try {
      await api.post(`/sourcing/connectors/${name}/configure`, { api_key: apiKey });
      toast.success(`${name} connector configured (runs SIMULATED until partner API goes live)`);
      setKeyFor(null); setApiKey(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runImport = async (connector) => {
    if (!importFor.requisition_id) return toast.error("Pick a requisition");
    setBusy(connector);
    try {
      const { data } = await api.post("/sourcing/import", { connector, requisition_id: importFor.requisition_id, count: 3 });
      toast.success(`${data.imported} candidates imported from ${connector} into the pipeline`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runWebImport = async () => {
    if (!webUrl || !webReq) return toast.error("URL and requisition required");
    setBusy("web_import");
    try {
      const { data } = await api.post("/sourcing/web-import", { url: webUrl, requisition_id: webReq });
      toast.success(`Pulled ${data.chars_extracted.toLocaleString()} chars — candidate '${data.candidate.name}' created`);
      setWebUrl(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const postJob = async () => {
    if (!postReq || postBoards.length === 0) return toast.error("Pick requisition and boards");
    try {
      await api.post("/sourcing/post", { requisition_id: postReq, boards: postBoards });
      toast.success(`Posted to ${postBoards.join(", ")} (SIMULATED)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="sourcing-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Talent Acquisition</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1">Sourcing</h1>
      <p className="text-sm text-muted-foreground mt-1">Job board connectors, candidate import and source performance — talentrecruit-style.</p>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
        {connectors.map((c) => (
          <div key={c.name} data-testid={`connector-${c.name}`} className="border bg-white rounded-xl p-5 flex flex-col">
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold tracking-tight">{c.label}</div>
              <span className={`text-[9px] uppercase tracking-wider px-2 py-1 rounded-md font-bold ${c.mode === "LIVE" ? "bg-[#DDEAE3] text-[#133E31]" : "bg-amber-100 text-amber-800"}`}>{c.mode}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed flex-1">{c.description}</p>
            <div className="text-xs mt-2"><span className="font-bold text-[#0A251D]">{c.candidates_imported}</span> candidates sourced</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {c.needs_key && !c.configured && (
                <button data-testid={`connect-${c.name}`} onClick={() => setKeyFor(keyFor === c.name ? null : c.name)}
                  className="flex items-center gap-1.5 text-xs font-bold border border-[#0A251D] px-3 py-1.5 hover:bg-[#0A251D] hover:text-white transition-colors duration-150"><KeyRound size={12} /> Connect</button>
              )}
              {c.needs_key && c.configured && <span className="text-xs text-[#133E31] font-bold flex items-center gap-1"><Link2 size={12} /> Connected</span>}
              {["naukri", "linkedin", "indeed", "iimjobs", "referrals"].includes(c.name) && (
                <button data-testid={`import-${c.name}`} onClick={() => runImport(c.name)} disabled={busy === c.name}
                  className="flex items-center gap-1.5 text-xs font-bold bg-[#E05A47] text-white px-3 py-1.5 hover:bg-[#C74936] transition-colors duration-150 disabled:opacity-60">
                  <Download size={12} /> {busy === c.name ? "Importing…" : "Import 3"}
                </button>
              )}
            </div>
            {keyFor === c.name && (
              <div className="mt-3 flex gap-2">
                <input data-testid={`key-input-${c.name}`} value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Partner API key" className="border px-3 py-1.5 text-xs flex-1" />
                <button data-testid={`key-save-${c.name}`} onClick={() => configure(c.name)} className="bg-[#0A251D] text-white px-3 py-1.5 text-xs font-bold hover:bg-[#133E31] transition-colors duration-150">Save</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="border bg-white rounded-xl p-6" data-testid="web-import-panel">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1"><Globe size={13} /> Web Import — LIVE pull</div>
          <p className="text-xs text-muted-foreground mb-3">Paste any public URL (portfolio, GitHub profile, published resume) — TalentOS pulls the content and creates a candidate ready for AI screening.</p>
          <input data-testid="web-import-url" value={webUrl} onChange={(e) => setWebUrl(e.target.value)} placeholder="https://…" className="w-full border px-3 py-2 text-sm mb-2" />
          <div className="flex gap-2">
            <select data-testid="web-import-req" value={webReq} onChange={(e) => setWebReq(e.target.value)} className="border px-3 py-2 text-sm bg-white flex-1">
              {reqs.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
            <button data-testid="web-import-run" onClick={runWebImport} disabled={busy === "web_import"}
              className="bg-[#0A251D] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-60">
              {busy === "web_import" ? "Pulling…" : "Pull Candidate"}
            </button>
          </div>
        </div>

        <div className="border bg-white rounded-xl p-6" data-testid="post-job-panel">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-1"><Send size={13} /> Post Requisition to Boards</div>
          <p className="text-xs text-muted-foreground mb-3">Publish an open role across boards in one click (simulated until partner APIs are live).</p>
          <select data-testid="post-req-select" value={postReq} onChange={(e) => setPostReq(e.target.value)} className="w-full border px-3 py-2 text-sm bg-white mb-2">
            {reqs.map((r) => <option key={r.id} value={r.id}>{r.title}{r.posted_boards?.length > 1 ? ` (posted: ${r.posted_boards.join(", ")})` : ""}</option>)}
          </select>
          <div className="flex flex-wrap gap-2 mb-3">
            {["naukri", "linkedin", "indeed", "iimjobs"].map((b) => (
              <button key={b} onClick={() => setPostBoards((p) => p.includes(b) ? p.filter((x) => x !== b) : [...p, b])}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors duration-150 ${postBoards.includes(b) ? "bg-[#0A251D] text-white border-[#0A251D]" : "bg-white hover:border-[#0A251D]"}`}>{b}</button>
            ))}
          </div>
          <button data-testid="post-job-button" onClick={postJob}
            className="bg-[#E05A47] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#C74936] transition-colors duration-150">Post Now</button>
        </div>
      </div>

      <div className="border bg-white rounded-xl mt-6 overflow-x-auto" data-testid="source-analytics">
        <div className="px-5 pt-4 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Source Performance</div>
        <table className="w-full text-sm mt-2">
          <thead><tr className="border-b bg-[#F2F5F1] text-left">
            {["Source", "Candidates", "Avg AI Score", "Shortlisted+", "Hired"].map((h) => <th key={h} className="px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold">{h}</th>)}
          </tr></thead>
          <tbody>
            {analytics.map((s) => (
              <tr key={s.source} className="border-b last:border-0 hover:bg-[#F5F8F4] transition-colors duration-100">
                <td className="px-5 py-3 font-semibold capitalize">{s.source.replace(/_/g, " ")}</td>
                <td className="px-5 py-3">{s.count}</td>
                <td className="px-5 py-3">{s.avg_score ?? "—"}</td>
                <td className="px-5 py-3">{s.shortlisted_plus}</td>
                <td className="px-5 py-3 font-bold text-[#133E31]">{s.hired}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
